import React from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

/**
 * Props para FooterStatus
 */
export interface FooterStatusProps {
  /**
   * Número de campos que aún faltan por completar
   */
  missingCount: number;
  /**
   * Texto cuando faltan campos. Usa {count} como placeholder del número.
   * @default "Faltan {count} campos para continuar"
   */
  pendingText?: string;
  /**
   * Texto cuando todos los campos están completos.
   * @default "¡Listo! Puedes continuar ahora o completar los datos adicionales."
   */
  readyText?: string;
}

/**
 * Componente FooterStatus - Indicador de progreso para formularios
 * 
 * Muestra el estado de completitud de un formulario con indicadores visuales:
 * - Amarillo/Ámbar cuando faltan campos
 * - Verde cuando está completo
 * 
 * @example
 * ```tsx
 * <FooterStatus 
 *   missingCount={2}
 *   pendingText="Faltan {count} campos para crear el cliente"
 *   readyText="¡Listo! Puedes crear el cliente ahora."
 * />
 * ```
 */
export const FooterStatus: React.FC<FooterStatusProps> = ({
  missingCount,
  pendingText = 'Faltan {count} campos para continuar',
  readyText = '¡Listo! Puedes continuar ahora o completar los datos adicionales.',
}) => {
  const isReady = missingCount === 0;

  const resolvedPendingText = pendingText.replace('{count}', String(missingCount));

  if (isReady) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[color:var(--success-border)] bg-success-bg px-3 py-2">
        <CheckCircle2
          size={15}
          className="shrink-0 text-success"
        />
        <p className="text-xs font-medium text-secondary">
          <strong className="font-semibold">¡Listo!</strong>{' '}
          {readyText.replace('¡Listo! ', '')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-[color:var(--warning-border)] bg-warning-bg px-3 py-2">
      <AlertCircle
        size={15}
        className="shrink-0 text-warning"
      />
      <p className="text-xs font-medium text-secondary">
        Faltan{' '}
        <strong className="font-semibold">
          {missingCount} {missingCount === 1 ? 'campo' : 'campos'}
        </strong>{' '}
        {resolvedPendingText.replace(/^Faltan \d+ campos? /, '')}
      </p>
    </div>
  );
};
